import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUrlRecurso } from "@/hooks/useUrlRecurso";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ModalFormHeader, MODAL_FOOTER_CLS } from "@/components/ui/modal-form";
import { VisorArchivo } from "@/components/ui/zoom-archivo";

/**
 * ModalViewer - modal que muestra ÚNICAMENTE un recurso visual (PDF, imagen, doc).
 *
 * Agnóstico al tipo de archivo. Resuelve rutas del bucket privado
 * `firmas-digitales` y las URLs de Storage del proyecto a URL firmada (helper central
 * `@/lib/urlFirmada`) y documentos Mifiel vía Edge Function, ambos en `useUrlRecurso`.
 * Header/footer estándar (Descargar / Cerrar). Componente padre reutilizable (piloto: portal de agentes).
 *
 * Para recurso visual + información escrita al lado, ver `ModalViewerDetail`.
 */

const esStorage = (url: string) => /\/storage\/v1\/object\//.test(url);

/**
 * Storage ignora el atributo `download` (otro origen) y el navegador abría el archivo en otra
 * pestaña: `?download` hace que Storage lo mande como adjunto y se queda en la plataforma.
 */
function urlDescarga(url: string): string {
  if (!esStorage(url) || /[?&]download(=|&|$)/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}download`;
}

export interface ModalViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title?: string;
}

export function ModalViewer({ open, onOpenChange, url, title = "Documento" }: ModalViewerProps) {
  const isMobile = useIsMobile();
  const { url: effectiveUrl, loading, error } = useUrlRecurso(open, url);

  const body = (
    <div className="min-h-0 flex-1 overflow-hidden bg-muted">
      {loading ? (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      ) : (
        // Imagen → zoom solo sobre la foto (rueda/pellizco), no sobre la página; PDF → visor nativo.
        <VisorArchivo src={effectiveUrl} nombre={url} title={title} loading="lazy" />
      )}
    </div>
  );

  const footer = (
    <div className={MODAL_FOOTER_CLS}>
      <Button variant="cancel" onClick={() => onOpenChange(false)}>Cerrar</Button>
      {effectiveUrl && !loading && (
        <Button variant="primary-outline" asChild>
          <a
            href={urlDescarga(effectiveUrl)}
            download
            {...(esStorage(effectiveUrl) ? {} : { target: "_blank", rel: "noopener noreferrer" })}
          > Descargar
          </a>
        </Button>
      )}
    </div>
  );

  const content = (
    <>
      <ModalFormHeader title={title} subtitle="Vista previa del documento" />
      {body}
      {footer}
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex h-[90vh] flex-col rounded-t-2xl p-0">
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("flex h-[90vh] max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden p-0")}>
        {content}
      </DialogContent>
    </Dialog>
  );
}
